'use client';

import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Home, Store, Building, Warehouse } from 'lucide-react';

const environmentTypes = {
  home: { name: 'Residential Home', icon: Home, color: 'hsl(var(--primary))' },
  supermarket: { name: 'Retail Supermarket', icon: Store, color: 'hsl(var(--chart-2))' },
  office: { name: 'Corporate Office', icon: Building, color: 'hsl(var(--chart-3))' },
  warehouse: { name: 'Industrial Warehouse', icon: Warehouse, color: 'hsl(var(--chart-4))' },
};

interface EnvironmentSidebarProps {
  selectedEnvironment: string;
  setSelectedEnvironment: (env: string) => void;
  sensors: any[];
}

export default function EnvironmentSidebar({
  selectedEnvironment,
  setSelectedEnvironment,
  sensors,
}: EnvironmentSidebarProps) {
  const currentEnvironment = environmentTypes[selectedEnvironment as keyof typeof environmentTypes];

  const getSensorStats = () => {
    const online = sensors.filter((s) => s.temperature !== null).length;
    const avgTemp = sensors.reduce((acc, s) => acc + s.temperature, 0) / sensors.length;
    const avgHumidity = sensors.reduce((acc, s) => acc + s.humidity, 0) / sensors.length;
    const avgAirQuality = sensors.reduce((acc, s) => acc + s.airQuality, 0) / sensors.length;

    return { online, total: sensors.length, avgTemp, avgHumidity, avgAirQuality };
  };

  const stats = getSensorStats();

  return (
    <div className="w-80 bg-background border-r border-border flex flex-col shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <h1 className="text-xl font-bold mb-2">IoT Monitor</h1>
        <p className="text-sm text-muted-foreground">3D Environmental Monitoring</p>
      </div>

      {/* Environment Selection */}
      <div className="p-4 border-b border-border">
        <label className="text-sm font-medium mb-2 block">Environment Type</label>
        <Select value={selectedEnvironment} onValueChange={setSelectedEnvironment}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(environmentTypes).map(([key, env]) => (
              <SelectItem key={key} value={key}>
                <div className="flex items-center gap-2">
                  <env.icon className="w-4 h-4" style={{ color: env.color }} />
                  {env.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats Overview */}
      <div className="p-4 border-b border-border">
        <h3 className="text-sm font-semibold mb-3">Overview</h3>
        <div className="grid grid-cols-2 gap-3">
          <Card className="border-border">
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Sensors Online</div>
              <div className="text-lg font-bold text-green-600">
                {stats.online}/{stats.total}
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Avg Temperature</div>
              <div className="text-lg font-bold text-blue-600">{stats.avgTemp.toFixed(1)}°C</div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Avg Humidity</div>
              <div className="text-lg font-bold text-blue-600">{stats.avgHumidity.toFixed(1)}%</div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Air Quality</div>
              <div className="text-lg font-bold text-green-600">{stats.avgAirQuality.toFixed(0)}</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Sensors List */}
      <div className="flex-1 overflow-auto p-4">
        <h3 className="text-sm font-semibold mb-3">Sensors</h3>
        <div className="space-y-2">
          {sensors.map((sensor) => (
            <Card
              key={sensor.id}
              className="cursor-pointer transition-all duration-200 hover:bg-accent/50 border-border"
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-sm">{sensor.name}</div>
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <div>{sensor.temperature}°C</div>
                  <div>{sensor.humidity}%</div>
                  <div>AQI {sensor.airQuality}</div>
                  <div>{sensor.light}lux</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export { environmentTypes };
